/** Sell-side rate cards: scope, target, status, effective window, units priced. Each card's name opens it above the list. */

import React, { useMemo, useState } from 'react';
import { EmptyState, StatusPill, tableStyles } from '../../ui';
import FilterChipGroup from '../FilterChipGroup';
import type { AdvertiserRecord, MediaPlanAgency, MediaPlanCategory, MediaPlanRateCard, RateCardScope, RateCardStatus } from '../MediaPlans/types';
import { CARD_STATUS_LABELS, SCOPE_LABELS } from './rateCardDraft';
import { fmtDate, mutedText } from '../MediaPlans/formStyles';

interface Props {
  cards: MediaPlanRateCard[];
  categories: MediaPlanCategory[];
  agencies: MediaPlanAgency[];
  advertisers: AdvertiserRecord[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

type ScopeFilter = 'all' | RateCardScope;
type StatusFilter = 'all' | RateCardStatus;

const SCOPE_OPTIONS: Array<{ value: ScopeFilter; label: string }> = [
  { value: 'all', label: 'All scopes' },
  { value: 'default', label: 'Default' },
  { value: 'category', label: 'Category' },
  { value: 'agency', label: 'Agency' },
  { value: 'advertiser', label: 'Advertiser' },
];
const STATUS_OPTIONS: Array<{ value: StatusFilter; label: string }> = [
  { value: 'active', label: 'Active' },
  { value: 'draft', label: 'Draft' },
  { value: 'archived', label: 'Archived' },
  { value: 'all', label: 'All' },
];

export const STATUS_VARIANT: Record<RateCardStatus, 'ok' | 'draft' | 'paused'> = { active: 'ok', draft: 'draft', archived: 'paused' };

/** The card's list status as a small pill: an interim list reads quietly; only an explicit placeholder is flagged. */
export const ListStatusPill: React.FC<{ card: Pick<MediaPlanRateCard, 'listStatus'> }> = ({ card }) =>
  card.listStatus === 'interim' ? (
    <StatusPill variant="draft" appearance="quiet" size="sm" noDot>
      Interim list
    </StatusPill>
  ) : card.listStatus === 'placeholder' ? (
    <StatusPill variant="flagged" size="sm" noDot>
      Placeholder
    </StatusPill>
  ) : null;

export function cardTargetLabel(card: MediaPlanRateCard, categories: MediaPlanCategory[], agencies: MediaPlanAgency[], advertisers: AdvertiserRecord[]): string {
  switch (card.scope) {
    case 'category':
      return categories.find((c) => c.key === card.categoryKey)?.label || card.categoryKey || '—';
    case 'agency':
      return agencies.find((a) => a.key === card.agencyKey)?.name || card.agencyKey || '—';
    case 'advertiser':
      return advertisers.find((a) => a.key === card.advertiserKey)?.name || card.advertiserKey || '—';
    default:
      return 'Everyone';
  }
}

const RateCardList: React.FC<Props> = ({ cards, categories, agencies, advertisers, selectedId, onSelect }) => {
  const [scope, setScope] = useState<ScopeFilter>('all');
  const [status, setStatus] = useState<StatusFilter>('active');
  const filtered = useMemo(
    () =>
      [...cards]
        .filter((c) => (scope === 'all' || c.scope === scope) && (status === 'all' || c.status === status))
        .sort((a, b) => (a.effectiveFrom < b.effectiveFrom ? 1 : a.effectiveFrom > b.effectiveFrom ? -1 : a.name.localeCompare(b.name))),
    [cards, scope, status],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <FilterChipGroup<ScopeFilter> options={SCOPE_OPTIONS} value={scope} onChange={setScope} size="sm" />
        <FilterChipGroup<StatusFilter> options={STATUS_OPTIONS} value={status} onChange={setStatus} size="sm" />
      </div>
      {filtered.length === 0 ? (
        <EmptyState title="No rate cards match" subtitle="Try another scope or status filter." />
      ) : (
        <div style={tableStyles.wrapScroll}>
          <table style={tableStyles.table}>
            <thead>
              <tr>
                <th style={tableStyles.th}>Rate card</th>
                <th style={tableStyles.th}>Scope</th>
                <th style={tableStyles.th}>Applies to</th>
                <th style={tableStyles.th}>Status</th>
                <th style={tableStyles.th}>Effective</th>
                <th style={tableStyles.thNum}>Units priced</th>
                <th style={tableStyles.thNum}>Version</th>
                <th style={tableStyles.th}>Updated</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const selected = c.id === selectedId;
                const priced = Object.values(c.units).filter((u) => u && (typeof u.cpm === 'number' || typeof u.flatUsd === 'number')).length;
                return (
                  <tr key={c.id} style={selected ? tableStyles.trExpanded : undefined}>
                    <td style={tableStyles.td}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <button type="button" style={styles.nameButton} aria-pressed={selected} onClick={() => onSelect(c.id)}>
                          {c.name}
                        </button>
                        <ListStatusPill card={c} />
                      </div>
                      <div style={mutedText}>{c.id}</div>
                    </td>
                    <td style={tableStyles.td}>{SCOPE_LABELS[c.scope]}</td>
                    <td style={tableStyles.td}>{cardTargetLabel(c, categories, agencies, advertisers)}</td>
                    <td style={tableStyles.td}>
                      <StatusPill variant={STATUS_VARIANT[c.status]} size="sm">
                        {CARD_STATUS_LABELS[c.status]}
                      </StatusPill>
                    </td>
                    <td style={tableStyles.td}>
                      {fmtDate(c.effectiveFrom)}
                      {c.effectiveTo ? ` – ${fmtDate(c.effectiveTo)}` : ' →'}
                    </td>
                    <td style={tableStyles.tdNum}>{priced}</td>
                    <td style={tableStyles.tdNum}>v{c.version}</td>
                    <td style={tableStyles.td}>
                      {fmtDate(c.updatedAt)}
                      <div style={mutedText}>{c.updatedBy?.email?.split('@')[0]}</div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  nameButton: {
    border: 'none',
    background: 'transparent',
    padding: 0,
    cursor: 'pointer',
    fontFamily: 'var(--font-sans)',
    fontSize: 13.5,
    fontWeight: 700,
    color: 'var(--fg-1)',
    textAlign: 'left',
  },
};

export default RateCardList;
